/**
 * Specialist Pro Dispute Workspace
 * 
 * Full-featured dispute control panel for Specialist Pro tier users.
 * Provides access to all 8 strategy modes, template selection,
 * findings review, evidence review, and package preview.
 * 
 * Layout Zones:
 * 1. Control Zone - Strategy, template, round controls
 * 2. Findings Zone - Findings list with severity, evidence indicators
 * 3. Evidence Zone - Metro 2 codes, extracted values, source snippets
 * 4. Preview Zone - Package preview, letter preview, export actions
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useReportData } from '../../context/ReportDataContext';
import {
  metro2Service,
  STRATEGY_MODE_INFO,
  TEMPLATE_FAMILIES,
  ROUND_STATUS_INFO
} from '../../services/metro2Service';
import { cachedFetch, fetchCacheKey, invalidateFetchCache } from '../../utils/fetchCache';
import {
  Settings,
  FileText,
  Search,
  Eye,
  Download,
  Loader2,
  Plus,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Info,
  Building2,
  Target,
  Layers,
  FolderOpen,
  User,
  Briefcase,
  CreditCard,
  Code,
  FileCode,
  Zap,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { toast } from 'sonner';

// Strategy mode icons
const STRATEGY_ICONS = {
  bureau_package: Building2,
  single_item: Target,
  grouped: Layers,
  dispute_type: FolderOpen,
  inquiry_only: Search,
  personal_info_only: User,
  accounts_special: AlertTriangle,
  collections_exclusive: Briefcase,
};

// Bureau colors
const BUREAU_COLORS = {
  TransUnion: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Experian: 'bg-amber-100 text-amber-700 border-amber-200',
  Equifax: 'bg-red-100 text-red-700 border-red-200',
};

// Confidence score color
const getConfidenceColor = (score) => {
  if (score >= 0.8) return 'text-emerald-600 bg-emerald-50';
  if (score >= 0.6) return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
};

// Priority badge
const getPriorityBadge = (score) => {
  if (score >= 0.8) return <Badge className="bg-red-100 text-red-700">High</Badge>;
  if (score >= 0.5) return <Badge className="bg-amber-100 text-amber-700">Medium</Badge>;
  return <Badge className="bg-slate-100 text-slate-600">Low</Badge>;
};

// Control Zone Component
const ControlZone = ({
  rounds,
  selectedRoundId,
  onSelectRound,
  onCreateRound,
  strategyMode,
  onStrategyChange,
  templateFamily,
  onTemplateChange,
  availableStrategies,
  availableTemplates,
  loading,
  packageCount,
  findingsCount,
}) => {
  return (
    <div className="bg-white border rounded-xl p-5 space-y-5" data-testid="control-zone">
      <h3 className="font-semibold text-stone-900 flex items-center gap-2">
        <Settings size={18} />
        Control Zone
      </h3>
      
      {/* Round selector */}
      <div className="space-y-2">
        <Label className="text-slate-700">Dispute Round</Label>
        <div className="flex gap-2">
          <Select value={selectedRoundId || ''} onValueChange={onSelectRound}>
            <SelectTrigger className="flex-1" data-testid="round-selector">
              <SelectValue placeholder="Select or create a round" />
            </SelectTrigger>
            <SelectContent>
              {rounds.map(round => (
                <SelectItem key={round.id} value={round.id}>
                  {round.id.slice(0, 8)} - {ROUND_STATUS_INFO[round.status]?.label || round.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={onCreateRound}
            disabled={loading}
            data-testid="create-round-btn"
          >
            <Plus size={18} />
          </Button>
        </div>
      </div>
      
      {/* Strategy mode selector */}
      <div className="space-y-2">
        <Label className="text-slate-700">Strategy Mode</Label>
        <Select value={strategyMode} onValueChange={onStrategyChange} disabled={!selectedRoundId}>
          <SelectTrigger data-testid="strategy-selector">
            <SelectValue placeholder="Select strategy" />
          </SelectTrigger>
          <SelectContent>
            {availableStrategies.map(mode => {
              const info = STRATEGY_MODE_INFO[mode];
              const Icon = STRATEGY_ICONS[mode] || Settings;
              return (
                <SelectItem key={mode} value={mode}>
                  <div className="flex items-center gap-2">
                    <Icon size={16} />
                    <span>{info?.name || mode}</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {strategyMode && STRATEGY_MODE_INFO[strategyMode] && (
          <p className="text-xs text-slate-500">{STRATEGY_MODE_INFO[strategyMode].description}</p>
        )}
      </div>
      
      {/* Template selector */}
      <div className="space-y-2">
        <Label className="text-slate-700">Template Family</Label>
        <Select value={templateFamily} onValueChange={onTemplateChange} disabled={!selectedRoundId}>
          <SelectTrigger data-testid="template-selector">
            <SelectValue placeholder="Select template" />
          </SelectTrigger>
          <SelectContent>
            {availableTemplates.map(family => {
              const info = TEMPLATE_FAMILIES[family];
              return (
                <SelectItem key={family} value={family}>
                  <div className="flex items-center gap-2">
                    <FileText size={16} />
                    <span>{info?.name || family}</span>
                    <Badge variant="outline" className="text-xs">
                      {info?.tone || 'Professional'}
                    </Badge>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {templateFamily && TEMPLATE_FAMILIES[templateFamily] && (
          <p className="text-xs text-slate-500">{TEMPLATE_FAMILIES[templateFamily].description}</p>
        )}
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 pt-3 border-t">
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <div className="text-2xl font-bold text-stone-900">{findingsCount}</div>
          <div className="text-xs text-slate-500">Findings</div>
        </div>
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <div className="text-2xl font-bold text-emerald-600">{packageCount}</div>
          <div className="text-xs text-slate-500">Packages</div>
        </div>
      </div>
    </div>
  );
};

// Finding Card Component
const FindingCard = ({ finding, isExpanded, onToggle, onSelect, isSelected }) => {
  const Icon = STRATEGY_ICONS[finding.context?.category] || FileText;
  
  return (
    <div
      className={`border rounded-lg overflow-hidden transition-all ${
        isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white'
      }`}
      data-testid={`finding-${finding.id}`}
    >
      <div
        className="p-4 cursor-pointer hover:bg-slate-50"
        onClick={onToggle}
      >
        <div className="flex items-start gap-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onSelect(finding.id)}
            onClick={(e) => e.stopPropagation()}
            className="mt-1"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon size={16} className="text-slate-500" />
              <span className="font-medium text-stone-900 truncate">{finding.rule_name}</span>
              {finding.bureau && (
                <Badge variant="outline" className={`text-xs ${BUREAU_COLORS[finding.bureau] || ''}`}>
                  {finding.bureau}
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-600 line-clamp-2">{finding.description}</p>
          </div>
          <div className="flex items-center gap-2">
            {getPriorityBadge(finding.priority_score)}
            <Badge className={`text-xs ${getConfidenceColor(finding.confidence_score)}`}>
              {Math.round(finding.confidence_score * 100)}%
            </Badge>
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-4 pb-4 border-t bg-slate-50">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-xs text-slate-500">Metro 2 Fields</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {finding.metro_field_codes?.map(code => (
                  <Badge key={code} variant="outline" className="text-xs font-mono">
                    {code}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Evidence IDs</Label>
              <p className="text-sm text-slate-700">{finding.evidence_ids?.length || 0} linked</p>
            </div>
          </div>
          {finding.deterministic_explanation && (
            <div className="mt-3">
              <Label className="text-xs text-slate-500">Rule Explanation</Label>
              <p className="text-sm text-slate-700 mt-1">{finding.deterministic_explanation}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Evidence Card Component
const EvidenceCard = ({ evidence }) => {
  return (
    <div className="border rounded-lg p-4 bg-white" data-testid={`evidence-${evidence.id}`}>
      {/* Order: Metro 2 code, Human field label, Exact extracted text, Source snippet, Rule explanation */}
      <div className="space-y-3">
        {/* 1. Metro 2 Code */}
        <div className="flex items-center gap-2">
          <Code size={16} className="text-slate-500" />
          <Badge variant="outline" className="font-mono">{evidence.metro_code}</Badge>
        </div>
        
        {/* 2. Human Field Label */}
        <div>
          <Label className="text-xs text-slate-500">Field</Label>
          <p className="font-medium text-stone-900">{evidence.human_label}</p>
        </div>
        
        {/* 3. Exact Extracted Text */}
        <div>
          <Label className="text-xs text-slate-500">Extracted Value</Label>
          <p className="text-sm text-slate-700 font-mono bg-slate-50 p-2 rounded">
            {evidence.raw_text || 'N/A'}
          </p>
        </div>
        
        {/* 4. Source Snippet (highlighted) */}
        {evidence.snippet_hash && (
          <div>
            <Label className="text-xs text-slate-500">Source Reference</Label>
            <p className="text-xs text-slate-500 bg-amber-50 p-2 rounded border-l-2 border-amber-400">
              Page {evidence.page_number || '?'} • Selector: {evidence.selector || 'Auto-detected'}
            </p>
          </div>
        )}
        
        {/* 5. Confidence */}
        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-xs text-slate-500">Parser Confidence</span>
          <Badge className={getConfidenceColor(evidence.parser_confidence || 0.5)}>
            {Math.round((evidence.parser_confidence || 0.5) * 100)}%
          </Badge>
        </div>
      </div>
    </div>
  );
};

// Package Card Component
const PackageCard = ({ pkg, onPreview, onDownload, isActive }) => {
  const findingsCount = pkg.findings_snapshot?.length || 0;
  
  return (
    <div
      className={`border rounded-lg p-4 cursor-pointer transition-all ${
        isActive ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
      onClick={() => onPreview(pkg)}
      data-testid={`package-${pkg.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-slate-500" />
          {pkg.target_bureau ? (
            <Badge className={BUREAU_COLORS[pkg.target_bureau]}>{pkg.target_bureau}</Badge>
          ) : (
            <span className="font-medium text-slate-700">{pkg.package_type}</span>
          )}
        </div>
        {pkg.letter_url && (
          <Badge className="bg-emerald-100 text-emerald-700">Letter Ready</Badge>
        )}
      </div>
      
      <div className="text-sm text-slate-600 mb-3">
        {findingsCount} finding{findingsCount !== 1 ? 's' : ''}
      </div>
      
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={(e) => { e.stopPropagation(); onPreview(pkg); }}>
          <Eye size={14} className="mr-1" />
          Preview
        </Button>
        {pkg.letter_url && (
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onDownload(pkg); }}>
            <Download size={14} />
          </Button>
        )}
      </div>
    </div>
  );
};

// Main Specialist Pro Workspace
const SpecialistProWorkspace = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { reportData } = useReportData();
  
  // State
  const [loading, setLoading] = useState(false);
  const [processingRound, setProcessingRound] = useState(false);
  const [generatingLetters, setGeneratingLetters] = useState(false);
  
  // Rounds
  const [rounds, setRounds] = useState([]);
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [roundData, setRoundData] = useState(null);
  
  // Config
  const [strategyMode, setStrategyMode] = useState('bureau_package');
  const [templateFamily, setTemplateFamily] = useState('standard');
  const [availableStrategies, setAvailableStrategies] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  
  // Findings & Evidence
  const [findings, setFindings] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [selectedFindings, setSelectedFindings] = useState([]);
  const [expandedFinding, setExpandedFinding] = useState(null);
  
  // Packages
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [letterContent, setLetterContent] = useState(null);

  const workspaceCacheRef = useRef({});
  const mountedRef = useRef(true);
  const roundDetailAbortRef = useRef(null);

  useEffect(() => () => {
    mountedRef.current = false;
    roundDetailAbortRef.current?.abort();
  }, []);

  const applyWorkspaceItem = useCallback((item) => {
    if (!item?.round) return;
    const roundId = item.round.id;
    workspaceCacheRef.current[roundId] = item;
    setRoundData(item.round);
    setFindings(item.findings || []);
    setPackages(item.packages || []);
    if (item.round?.strategy_mode) {
      setStrategyMode(item.round.strategy_mode);
    }
    if (item.packages?.length > 0) {
      setSelectedPackage(item.packages[0]);
    } else {
      setSelectedPackage(null);
    }
  }, []);
  
  // Load tier info and rounds on mount
  useEffect(() => {
    loadTierInfo();
    loadWorkspace();
  }, []);
  
  // Hydrate selected round from batch cache (no per-round getRound on selection)
  useEffect(() => {
    if (!selectedRoundId) return;
    const cached = workspaceCacheRef.current[selectedRoundId];
    if (cached) {
      applyWorkspaceItem(cached);
      return;
    }
    loadRoundData(selectedRoundId, { force: true });
  }, [selectedRoundId, applyWorkspaceItem]);
  
  const loadTierInfo = async () => {
    try {
      const tier = user?.subscription_tier || 'specialist_pro';
      const tierInfo = await metro2Service.getTierInfo(tier, token);
      setAvailableStrategies(tierInfo.available_strategy_modes || []);
      
      const templatesInfo = await metro2Service.getTemplates(tier, token);
      setAvailableTemplates(templatesInfo.available_templates || ['standard']);
    } catch (error) {
      console.error('Failed to load tier info:', error);
      // Fallback defaults for specialist_pro
      setAvailableStrategies([
        'bureau_package', 'single_item', 'grouped', 'dispute_type',
        'inquiry_only', 'personal_info_only', 'accounts_special', 'collections_exclusive'
      ]);
      setAvailableTemplates(['standard', 'aggressive', 'compliance', 'escalation']);
    }
  };
  
  const loadWorkspace = async () => {
    try {
      const userId = user?.user_id || user?.id;
      if (!userId || !token) return;

      const cacheKey = fetchCacheKey('specialist-pro-workspace', userId);
      const result = await cachedFetch(
        cacheKey,
        (signal) => metro2Service.getSpecialistProWorkspace(userId, token, { limit: 20, signal }),
        { ttlMs: 20000 }
      );

      if (!mountedRef.current) return;

      const items = result?.items || [];
      const nextRounds = items.map((item) => item.round).filter(Boolean);
      setRounds(nextRounds);

      items.forEach((item) => {
        const roundId = item?.round?.id;
        if (roundId) {
          workspaceCacheRef.current[roundId] = item;
        }
      });

      if (nextRounds.length > 0 && !selectedRoundId) {
        setSelectedRoundId(nextRounds[0].id);
      }
    } catch (error) {
      console.error('Failed to load specialist pro workspace:', error);
      if (mountedRef.current) {
        toast.error('Failed to load dispute workspace');
      }
    }
  };
  
  const loadRoundData = async (roundId, { force = false } = {}) => {
    if (!roundId || !token) return;

    if (!force) {
      const cached = workspaceCacheRef.current[roundId];
      if (cached) {
        applyWorkspaceItem(cached);
        return;
      }
    }

    roundDetailAbortRef.current?.abort();
    const controller = new AbortController();
    roundDetailAbortRef.current = controller;

    setLoading(true);
    try {
      const cacheKey = fetchCacheKey('specialist-pro-round-detail', roundId);
      const result = await cachedFetch(
        cacheKey,
        () => metro2Service.getRound(roundId, token),
        { ttlMs: 15000, force, signal: controller.signal }
      );

      if (!mountedRef.current || controller.signal.aborted) return;

      const item = {
        round: result.round,
        findings: result.findings || [],
        packages: result.packages || [],
        findings_count: (result.findings || []).length,
        packages_count: (result.packages || []).length,
      };
      workspaceCacheRef.current[roundId] = item;
      applyWorkspaceItem(item);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('Failed to load round:', error);
      if (mountedRef.current) {
        toast.error('Failed to load round data');
      }
    } finally {
      if (mountedRef.current && !controller.signal.aborted) {
        setLoading(false);
      }
    }
  };
  
  const handleCreateRound = async () => {
    // Navigate to account selection first
    navigate('/credit-report/negative', { 
      state: { 
        returnTo: '/disputes/pro',
        mode: 'select_for_dispute',
        tier: 'specialist_pro'
      } 
    });
  };
  
  const handleProcessRound = async () => {
    if (!selectedRoundId) return;
    
    setProcessingRound(true);
    try {
      const result = await metro2Service.processRound(
        selectedRoundId,
        user?.user_id || user?.id,
        token
      );
      
      if (result.success) {
        toast.success(`Processed: ${result.findings_count} findings, ${result.packages_count} packages`);
        invalidateFetchCache(fetchCacheKey('specialist-pro-workspace', user?.user_id || user?.id));
        invalidateFetchCache(fetchCacheKey('specialist-pro-round-detail', selectedRoundId));
        loadRoundData(selectedRoundId, { force: true });
      } else {
        throw new Error(result.message || 'Processing failed');
      }
    } catch (error) {
      console.error('Process round error:', error);
      toast.error(error.message || 'Failed to process round');
    } finally {
      setProcessingRound(false);
    }
  };
  
  const handleGenerateLetters = async () => {
    if (!selectedRoundId) return;
    
    setGeneratingLetters(true);
    try {
      const result = await metro2Service.renderLetters(
        selectedRoundId,
        templateFamily,
        user?.user_id || user?.id,
        token
      );
      
      if (result.success) {
        toast.success(`Generated ${result.letters_rendered} letter(s)`);
        invalidateFetchCache(fetchCacheKey('specialist-pro-round-detail', selectedRoundId));
        loadRoundData(selectedRoundId, { force: true });
      } else {
        throw new Error(result.message || 'Letter generation failed');
      }
    } catch (error) {
      console.error('Generate letters error:', error);
      toast.error(error.message || 'Failed to generate letters');
    } finally {
      setGeneratingLetters(false);
    }
  };
  
  const handlePreviewPackage = async (pkg) => {
    setSelectedPackage(pkg);
    
    if (pkg.letter_url && selectedRoundId) {
      try {
        const result = await metro2Service.getLetter(selectedRoundId, pkg.id, token);
        setLetterContent(result);
      } catch (error) {
        console.error('Failed to load letter:', error);
        setLetterContent(null);
      }
    } else {
      setLetterContent(null);
    }
  };
  
  const handleDownloadLetter = (pkg) => {
    if (!letterContent?.content) {
      toast.error('Letter content not available');
      return;
    }
    
    const blob = new Blob([letterContent.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispute_letter_${pkg.target_bureau || 'package'}_${selectedRoundId?.slice(0, 8)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Letter downloaded');
  };
  
  const toggleFindingSelection = (findingId) => {
    setSelectedFindings(prev => 
      prev.includes(findingId) 
        ? prev.filter(id => id !== findingId)
        : [...prev, findingId]
    );
  };
  
  return (
    <div className="min-h-screen bg-slate-100" data-testid="specialist-pro-workspace">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-screen-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/disputes')}>
                <ArrowLeft size={18} className="mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-stone-900">Dispute Workspace</h1>
                <p className="text-sm text-slate-500">Specialist Pro • Full Control</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {selectedRoundId && roundData?.status === 'draft' && (
                <Button
                  variant="outline"
                  onClick={handleProcessRound}
                  disabled={processingRound}
                  data-testid="process-round-btn"
                >
                  {processingRound ? (
                    <Loader2 size={18} className="animate-spin mr-2" />
                  ) : (
                    <Zap size={18} className="mr-2" />
                  )}
                  Process Round
                </Button>
              )}
              
              {selectedRoundId && packages.length > 0 && (
                <Button
                  onClick={handleGenerateLetters}
                  disabled={generatingLetters}
                  className="btn-primary"
                  data-testid="generate-letters-btn"
                >
                  {generatingLetters ? (
                    <Loader2 size={18} className="animate-spin mr-2" />
                  ) : (
                    <FileText size={18} className="mr-2" />
                  )}
                  Generate Letters
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column: Control + Findings */}
          <div className="col-span-4 space-y-6">
            {/* Control Zone */}
            <ControlZone
              rounds={rounds}
              selectedRoundId={selectedRoundId}
              onSelectRound={setSelectedRoundId}
              onCreateRound={handleCreateRound}
              strategyMode={strategyMode}
              onStrategyChange={setStrategyMode}
              templateFamily={templateFamily}
              onTemplateChange={setTemplateFamily}
              availableStrategies={availableStrategies}
              availableTemplates={availableTemplates}
              loading={loading}
              packageCount={packages.length}
              findingsCount={findings.length}
            />
            
            {/* Findings Zone */}
            <div className="bg-white border rounded-xl p-5" data-testid="findings-zone">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2 mb-4">
                <Search size={18} />
                Findings ({findings.length})
              </h3>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : findings.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <AlertTriangle size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No findings yet</p>
                  <p className="text-xs mt-1">Process the round to generate findings</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {findings.map(finding => (
                    <FindingCard
                      key={finding.id}
                      finding={finding}
                      isExpanded={expandedFinding === finding.id}
                      onToggle={() => setExpandedFinding(expandedFinding === finding.id ? null : finding.id)}
                      onSelect={toggleFindingSelection}
                      isSelected={selectedFindings.includes(finding.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Middle Column: Evidence */}
          <div className="col-span-4">
            <div className="bg-white border rounded-xl p-5 sticky top-24" data-testid="evidence-zone">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2 mb-4">
                <FileCode size={18} />
                Evidence Review
              </h3>
              
              {expandedFinding ? (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {/* Show evidence for expanded finding */}
                  <div className="text-sm text-slate-600 mb-4 p-3 bg-slate-50 rounded-lg">
                    <p className="font-medium">Evidence for: {findings.find(f => f.id === expandedFinding)?.rule_name}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {findings.find(f => f.id === expandedFinding)?.evidence_ids?.length || 0} evidence records
                    </p>
                  </div>
                  
                  {/* Mock evidence display - in real impl, fetch from API */}
                  {findings.find(f => f.id === expandedFinding)?.metro_field_codes?.map((code, idx) => (
                    <EvidenceCard
                      key={`${expandedFinding}-${code}-${idx}`}
                      evidence={{
                        id: `ev-${idx}`,
                        metro_code: code,
                        human_label: code === 'BS-7' ? 'Current Balance' :
                                     code === 'BS-17B' ? 'High Balance' :
                                     code === 'BS-10' ? 'Account Status' : code,
                        raw_text: 'Extracted from credit report',
                        parser_confidence: 0.95,
                        page_number: 1,
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Info size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Select a finding to view evidence</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Right Column: Packages + Preview */}
          <div className="col-span-4 space-y-6">
            {/* Packages Zone */}
            <div className="bg-white border rounded-xl p-5" data-testid="packages-zone">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2 mb-4">
                <Layers size={18} />
                Packages ({packages.length})
              </h3>
              
              {packages.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No packages yet</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {packages.map(pkg => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      onPreview={handlePreviewPackage}
                      onDownload={handleDownloadLetter}
                      isActive={selectedPackage?.id === pkg.id}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Letter Preview Zone */}
            <div className="bg-white border rounded-xl p-5" data-testid="preview-zone">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2 mb-4">
                <Eye size={18} />
                Letter Preview
              </h3>
              
              {selectedPackage && letterContent?.content ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge className={BUREAU_COLORS[selectedPackage.target_bureau] || 'bg-slate-100'}>
                      {selectedPackage.target_bureau || selectedPackage.package_type}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadLetter(selectedPackage)}
                    >
                      <Download size={14} className="mr-1" />
                      Download
                    </Button>
                  </div>
                  
                  <div 
                    className="border rounded-lg p-4 max-h-[400px] overflow-y-auto prose prose-sm"
                    dangerouslySetInnerHTML={{ __html: letterContent.content }}
                  />
                </div>
              ) : selectedPackage && !selectedPackage.letter_url ? (
                <div className="text-center py-8 text-slate-500">
                  <Info size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Letter not yet generated</p>
                  <p className="text-xs mt-1">Click "Generate Letters" to create</p>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Eye size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Select a package to preview</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecialistProWorkspace;
